import { NavTreeNode } from "@/utils/types";
import { validate as validateUuid } from "uuid";
import { useCanvasStore } from "../stores/canvasStore";
import { TempoElement } from "@/utils/tempoElement";
import { toBlob } from "html-to-image";

export const UNZOOM_MULTIPLIER_CSS_VAR = (max?: number, scale: number = 1) => {
  if (max === undefined) {
    return `calc(1 / var(--canvas-zoom) * ${scale})`;
  }

  return `calc(min(${max}, 1 / var(--canvas-zoom) * ${scale}))`;
};

export const getUnzoomedPixelValue = (px: number, max?: number) => {
  return `calc(${px}px * ${UNZOOM_MULTIPLIER_CSS_VAR(
    max === undefined ? px * 5 : max,
  )})`;
};

export const findElementByDomUniquePath = (
  iframeDocument?: Document,
  uniquePath?: string,
): HTMLElement | null => {
  if (!uniquePath || !iframeDocument) return null;

  const pathIndices = uniquePath.split("-").slice(1); // Remove the leading "root"
  let currentElement = iframeDocument.body as HTMLElement;

  for (const indexStr of pathIndices) {
    const index = parseInt(indexStr);
    if (currentElement.children[index]) {
      currentElement = currentElement.children[index] as HTMLElement;
    } else {
      return null;
    }
  }

  return currentElement;
};

export const getDomUniquePathFromNode = (node: HTMLElement): string => {
  const path: string[] = [];
  let currentNode: HTMLElement | null = node;
  // The unique path is the index of the node in the parent overall chain to the body
  while (
    currentNode &&
    currentNode.tagName?.toLowerCase() !== "body" &&
    !currentNode?.hasAttribute("data-tempo-asset-container")
  ) {
    // Filter out scripts (devtools does the same)
    const index = Array.from(currentNode.parentElement?.children || [])
      .filter((child) => child.tagName?.toLowerCase() !== "script")
      .indexOf(currentNode);
    if (index === -1) {
      path.push("0");
    } else {
      path.push(index.toString());
    }
    currentNode = currentNode.parentElement;
  }

  // The tempo asset container adds an extra level compared application storyboards that just are relative to the body
  if (currentNode?.hasAttribute("data-tempo-asset-container")) {
    path.push("0");
  }

  return "0-" + path.reverse().join("-");
};

export const getCodebaseIdFromNode = (node: HTMLElement): string | null => {
  if (!node) {
    return null;
  }

  const tempoElementId = node?.getAttribute("data-tempoelementid");
  if (tempoElementId) {
    return tempoElementId;
  }

  if (node?.tagName?.toLowerCase() == "body") {
    return "body";
  }

  if (node?.id == "root") {
    return "root";
  }

  if (node?.id == "__next") {
    return "__next";
  }

  return null;
};

export const getNavNodeForDomElement = (
  domElement: HTMLElement,
  storyboardId: string,
): NavTreeNode | null => {
  const domUniquePath = getDomUniquePathFromNode(domElement);
  const codebaseId = getCodebaseIdFromNode(domElement);

  if (codebaseId) {
    const elementKey = new TempoElement(
      codebaseId,
      storyboardId,
      domUniquePath,
    );
    return useCanvasStore.getState().domUniquePathElementKeyLookup[
      elementKey.getKey()
    ];
  }

  return null;
};

export const getIframeDocumentForStoryboard = (
  storyboardId: string,
  applicationStoryboards: Record<string, { iframeId: string }>,
): { iframeElement: HTMLIFrameElement; contentDocument: Document } | null => {
  // Only application storyboards have accessible DOM in their iframes
  const appStoryboard = applicationStoryboards[storyboardId];
  if (!appStoryboard) return null;

  const iframeElement = document.getElementById(
    appStoryboard.iframeId,
  ) as HTMLIFrameElement;
  if (!iframeElement?.contentDocument || !iframeElement?.contentWindow)
    return null;

  return { iframeElement, contentDocument: iframeElement.contentDocument };
};

export function leadingDebounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number,
  options: {
    leadingCount?: number;
    trailing?: boolean;
    maxWait?: number;
  } = {},
) {
  const { leadingCount = 5, trailing = true, maxWait } = options;

  let timeout: NodeJS.Timeout | null = null;
  let maxTimeout: NodeJS.Timeout | null = null;
  let callCount = 0;
  let lastCallTime = 0;
  let lastInvokeTime = 0;
  let lastArgs: Parameters<T> | null = null;
  let lastThis: any = null;

  function invokeFunc(time: number) {
    const args = lastArgs!;
    const thisArg = lastThis;

    lastArgs = lastThis = null;
    lastInvokeTime = time;
    func.apply(thisArg, args);
  }

  function startTimer(timerFunc: () => void, wait: number) {
    return setTimeout(timerFunc, wait);
  }

  function cancelTimer(id: NodeJS.Timeout | null) {
    if (id) clearTimeout(id);
  }

  function trailingEdge() {
    timeout = null;

    if (trailing && lastArgs) {
      invokeFunc(Date.now());
    }

    callCount = 0; // Reset for next burst
  }

  function timerExpired() {
    const time = Date.now();

    if (maxWait !== undefined) {
      const timeSinceLastInvoke = time - lastInvokeTime;
      if (timeSinceLastInvoke >= maxWait) {
        return trailingEdge();
      }
    }

    trailingEdge();
  }

  function maxWaitExpired() {
    maxTimeout = null;

    if (lastArgs) {
      invokeFunc(Date.now());

      // Restart the regular debounce timer
      if (timeout) cancelTimer(timeout);
      timeout = startTimer(timerExpired, wait);
    }
  }

  return function (this: any, ...args: Parameters<T>) {
    const now = Date.now();
    const isInvoking = lastCallTime === 0 || now - lastCallTime > wait;

    lastArgs = args;
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    lastThis = this;
    lastCallTime = now;

    // Reset counter if enough time has passed since last call
    if (isInvoking) {
      callCount = 0;
    }

    // Fire immediately if we're within the leading count
    if (callCount < leadingCount) {
      callCount++;
      invokeFunc(now);

      // Start maxWait timer if this is the first call
      if (maxWait !== undefined && callCount === 1) {
        cancelTimer(maxTimeout);
        maxTimeout = startTimer(maxWaitExpired, maxWait);
      }
    } else {
      // Normal debounce behavior after leading events
      cancelTimer(timeout);
      timeout = startTimer(timerExpired, wait);

      // Start maxWait timer if not already running
      if (maxWait !== undefined && !maxTimeout) {
        maxTimeout = startTimer(maxWaitExpired, maxWait);
      }
    }
  };
}

/**
 * Checks if a DOM element is capturable (has non-zero dimensions).
 * Use this when you already have the element to avoid redundant DOM lookups.
 */
export function hasCapturableDom(el: HTMLElement | null): boolean {
  if (!el) return false;
  const rect = el.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
}

/**
 * Checks if a DOM element at the given path is capturable.
 * Uses domUniquePath to find the exact element.
 *
 * Note: If you need to call multiple functions on the same element,
 * use findElementByDomUniquePath once and pass the element to the
 * element-based versions (hasCapturableDom, getElementDimensions, etc.)
 *
 * @returns true if:
 * - The element exists in the DOM at the specified path
 * - The element has non-zero dimensions (is rendered and visible)
 */
export function hasCapturableDomByPath(
  contentWindow: WindowProxy | undefined,
  domUniquePath: string | undefined,
): boolean {
  if (!contentWindow?.document || !domUniquePath) return false;
  const el = findElementByDomUniquePath(contentWindow.document, domUniquePath);
  return hasCapturableDom(el);
}

/**
 * Gets the bounding dimensions of a DOM element.
 * Use this when you already have the element to avoid redundant DOM lookups.
 */
export function getElementDimensions(
  el: HTMLElement | null,
): { width: number; height: number } | null {
  if (!el) return null;
  const rect = el.getBoundingClientRect();
  return { width: rect.width, height: rect.height };
}

/**
 * Gets the bounding dimensions of a DOM element by its unique path.
 *
 * @returns { width, height } or null if element not found
 */
export function getElementDimensionsByPath(
  contentWindow: WindowProxy,
  domUniquePath: string | undefined,
): { width: number; height: number } | null {
  if (!contentWindow.document || !domUniquePath) return null;
  const el = findElementByDomUniquePath(contentWindow.document, domUniquePath);
  return getElementDimensions(el);
}

/**
 * Captures a screenshot of a DOM element.
 * Use this when you already have the element to avoid redundant DOM lookups.
 */
export async function captureElementScreenshot(
  element: HTMLElement | null,
): Promise<Blob | null> {
  if (!element) return null;

  try {
    return await toBlob(element, {
      pixelRatio: 2,
      skipFonts: true,
      filter: (node: Element) =>
        node.tagName !== "SCRIPT" && node.tagName !== "STYLE",
    });
  } catch (error) {
    console.error("[Screenshot] Failed to capture element:", error);
    return null;
  }
}

/**
 * Captures a screenshot of a DOM element by its unique path.
 * Uses domUniquePath to find the exact element instead of querySelector.
 */
export async function captureElementScreenshotByPath(
  contentWindow: WindowProxy,
  domUniquePath: string | undefined,
): Promise<Blob | null> {
  if (!contentWindow.document || !domUniquePath) return null;

  const element = findElementByDomUniquePath(
    contentWindow.document,
    domUniquePath,
  );

  if (!element) {
    console.warn(
      "[Screenshot] No element found at domUniquePath:",
      domUniquePath,
    );
    return null;
  }

  return captureElementScreenshot(element);
}
